import { useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import { useTranslation } from "i18n-fe";

// #3604 (ADR 0004): the inline form Publish Results opens for a party with no
// USEF ID and no mailing address on file. USEF's uploadclass wants Address,
// City, State and Zip for such a party; report-api's buildPartyFields reads
// them from partyOverrides[rideId][role] (address, city, state, zip). All four
// are required here and state is a 2-letter code. The draft lives in this
// form; Save hands the trimmed values to the caller, which holds them per
// class in useFederationResults.
// #3683: `keys` narrows the form to what the server says is missing (a rider whose
// FVD People record has a street and city but no zip is asked for the zip only).
export const ADDRESS_KEYS = ["address", "city", "state", "zip"];
export default function PartyAddressForm({ testIdSuffix, onSave, onCancel, keys = ADDRESS_KEYS }) {
  const { t } = useTranslation("scoreadmin");
  const [values, setValues] = useState({ address: "", city: "", state: "", zip: "" });
  const [touched, setTouched] = useState(false);

  const trimmed = {
    address: values.address.trim(),
    city: values.city.trim(),
    state: values.state.trim().toUpperCase(),
    zip: values.zip.trim(),
  };
  const errors = {
    address: !trimmed.address,
    city: !trimmed.city,
    state: !/^[A-Z]{2}$/.test(trimmed.state),
    zip: !trimmed.zip,
  };
  const valid = keys.every((k) => !errors[k]);

  const field = (key, label, extra = {}) => (
    <TextField
      size="small"
      label={label}
      required
      value={values[key]}
      onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
      error={touched && errors[key]}
      helperText={
        touched && errors[key]
          ? key === "state"
            ? t("publishResults.addressStateInvalid")
            : t("publishResults.addressRequired")
          : undefined
      }
      slotProps={{ htmlInput: { "data-testid": `party-address-field-${key}-${testIdSuffix}`, ...extra.htmlInput } }}
      sx={extra.sx}
    />
  );

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (valid) onSave(Object.fromEntries(keys.map((k) => [k, trimmed[k]])));
      }}
      sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start", mt: 0.5 }}
      data-testid={`party-address-form-${testIdSuffix}`}
    >
      {keys.includes("address") && field("address", t("publishResults.addressStreet"), { sx: { minWidth: 220 } })}
      {keys.includes("city") && field("city", t("publishResults.addressCity"), { sx: { minWidth: 140 } })}
      {keys.includes("state") && field("state", t("publishResults.addressState"), { htmlInput: { maxLength: 2 }, sx: { width: 90 } })}
      {keys.includes("zip") && field("zip", t("publishResults.addressZip"), { sx: { width: 120 } })}
      <Button type="submit" size="small" variant="contained" data-testid={`party-address-save-${testIdSuffix}`}>
        {t("publishResults.addressSave")}
      </Button>
      <Button size="small" onClick={onCancel} data-testid={`party-address-cancel-${testIdSuffix}`}>
        {t("publishResults.addressCancel")}
      </Button>
    </Box>
  );
}
